import React, { useEffect, useId, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { OTP_CODE_LENGTH, defaultOtpChannel, type OtpChannel as OtpChannelName } from 'constants/otp';
import {
  fetchOtpStatus,
  otpChannelHint,
  otpChannelLabel,
  useResendOtp,
  useVerifyOtp,
  type OtpChannel,
  type OtpPurpose,
  type OtpVerifyResponse,
} from 'hooks/useOtpChallenge';
import { addNotification } from 'notifications';
import { Button, LetterIcon, OTPInput, PhoneIcon, ReloadIcon } from 'ui';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import classes from './OtpInlineBlock.module.scss';

function fallbackChannel(): OtpChannelName {
  return defaultOtpChannel();
}

const CHANNELS: { id: OtpChannel; label: string; hint: string; icon: React.ReactNode }[] = [
  {
    id: 'voice',
    label: 'Звонок',
    hint: 'Позвоним и робот продиктует код. Возьмите трубку.',
    icon: <PhoneIcon />,
  },
  {
    id: 'sms',
    label: 'SMS',
    hint: 'Код в SMS на ваш номер',
    icon: <LetterIcon />,
  },
];

function normalizeChannel(value: string | undefined): string {
  if (value === 'simpush') return 'sms';
  if (value === 'flashcall') return 'voice'; // legacy name of "Звонок"
  return value || '';
}

/** Render provider/API text with clickable links (e.g. VK community dialog). */
function renderWithLinks(text: string): React.ReactNode {
  return text.split(/(https?:\/\/[^\s]+)/g).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <a key={index} href={part} target='_blank' rel='noopener noreferrer'>
        {/vk\.(com|ru)/i.test(part) ? 'открыть диалог с сообществом ВК' : part}
      </a>
    ) : (
      part
    ),
  );
}

function isOtpExpiredMessage(message: string): boolean {
  // Only hard-lock when the challenge is gone / rate-limited - not on a single wrong code.
  return /код истек|код истек|не найден|слишком много попыток|нет идентификатора/i.test(
    message,
  );
}

/** Error text already tells the user to pick another way: the blue next-step hints would repeat it. */
function offersAlternative(message: string): boolean {
  return /другой способ|выберите|запросите/i.test(message);
}

function isWrongCodeMessage(message: string): boolean {
  return /неверн\w*\s+код|код подтверждения/i.test(message) && !isOtpExpiredMessage(message);
}

export type OtpInlineBlockProps = {
  guest?: boolean;
  purpose: OtpPurpose;
  phone?: string;
  challengeId?: string;
  channel?: string;
  cascadeAfter?: number;
  title?: string;
  /** Shown when the first send failed and there is no challenge yet. */
  initialError?: string;
  onVerified: (data: OtpVerifyResponse) => void;
  onAuthRequired?: () => void;
  /** 'screen' - отдельный экран кода по Figma 21:4837 / 21:10034 (заголовок рисует родитель). */
  variant?: 'inline' | 'screen';
  /** Ссылки под кнопками (например «Изменить номер») - только для variant='screen'. */
  footer?: React.ReactNode;
  /** Новый challenge после повторной отправки - родитель сохраняет его, чтобы F5 не терял код. */
  onChallengeChange?: (data: { challengeId: string; channel: string; cascadeAfter: number }) => void;
};

export const OtpInlineBlock: React.FC<OtpInlineBlockProps> = ({
  guest = false,
  purpose,
  phone,
  challengeId: initialChallengeId = '',
  channel: initialChannel,
  cascadeAfter = 45,
  title,
  initialError = '',
  onVerified,
  onAuthRequired,
  variant = 'inline',
  footer,
  onChallengeChange,
}) => {
  const screen = variant === 'screen';
  const resendLabel = screen ? 'Отправить еще раз' : 'Отправить снова';
  const tooltipId = useId();
  const channelHintId = useId();
  const [challengeId, setChallengeId] = useState(initialChallengeId);
  const [channel, setChannel] = useState(initialChannel || fallbackChannel());
  const [code, setCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(
    initialChallengeId ? cascadeAfter : initialError ? 0 : 45,
  );
  const [expired, setExpired] = useState(false);
  const [localError, setLocalError] = useState(initialError);
  const [inputKey, setInputKey] = useState(0);
  // After first wrong-code / send failure unlock resend + alternate channel immediately.
  const [actionsUnlocked, setActionsUnlocked] = useState(Boolean(initialError) && !initialChallengeId);
  const [deliveryNote, setDeliveryNote] = useState('');
  // Экран кода: ответ на повторную отправку - строкой на экране, а не всплывающим уведомлением.
  const [sentNote, setSentNote] = useState('');
  const autoCascadeKey = useRef('');

  const resend = useResendOtp(guest);
  const verify = useVerifyOtp(guest);

  useEffect(() => {
    setChallengeId(initialChallengeId);
    if (initialChannel) setChannel(initialChannel);
    if (initialChallengeId) {
      setSecondsLeft(cascadeAfter);
      setExpired(false);
      setCode('');
      setLocalError('');
      setActionsUnlocked(false);
      setInputKey((k) => k + 1);
    }
  }, [initialChallengeId, initialChannel, cascadeAfter]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = window.setTimeout(() => setSecondsLeft((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  const applyStart = (data: { challenge_id: string; channel: string; cascade_after?: number }) => {
    setChallengeId(data.challenge_id);
    setChannel(data.channel);
    setCode('');
    setExpired(false);
    setLocalError('');
    setSentNote('');
    setActionsUnlocked(false);
    setSecondsLeft(data.cascade_after || 45);
    setInputKey((k) => k + 1);
    onChallengeChange?.({
      challengeId: data.challenge_id,
      channel: data.channel,
      cascadeAfter: data.cascade_after || 45,
    });
  };

  const noteExpired = (message: string) => {
    if (!isOtpExpiredMessage(message)) return;
    setExpired(true);
    setSecondsLeft(0);
    setActionsUnlocked(true);
  };

  const unlockAfterFailure = (message: string) => {
    // Keep challenge (unless expired) and immediately offer resend / other method.
    setActionsUnlocked(true);
    if (!isOtpExpiredMessage(message)) {
      setSecondsLeft(0);
    }
  };

  const handleAuthError = (message: string): boolean => {
    if (!/сессия истекла|неверный токен|войдите/i.test(message)) return false;
    addNotification(message, 'error');
    onAuthRequired?.();
    return true;
  };

  const handleVerify = () => {
    if (!challengeId || code.length < OTP_CODE_LENGTH || expired) return;
    setLocalError('');
    verify.mutate(
      { challenge_id: challengeId, code },
      {
        onSuccess: (data) => {
          addNotification(data.message || 'Код принят', 'success');
          onVerified(data);
        },
        onError: (error) => {
          const message = getApiErrorMessage(error, 'Неверный код');
          if (handleAuthError(message)) return;
          noteExpired(message);
          setLocalError(message);
          // Wrong code / soft failure: keep challenge, clear input, unlock next actions now.
          if (isWrongCodeMessage(message) || !isOtpExpiredMessage(message)) {
            setCode('');
            setInputKey((k) => k + 1);
            unlockAfterFailure(message);
          }
        },
      },
    );
  };

  const sendCode = (next: OtpChannel, options?: { silent?: boolean }) => {
    const onError = (error: unknown) => {
      const message = getApiErrorMessage(error, 'Временно не работает. Попробуйте позже.');
      if (handleAuthError(message)) return;
      noteExpired(message);
      setLocalError(message);
      unlockAfterFailure(message);
    };
    resend.mutate(
      {
        challenge_id: challengeId || undefined,
        channel: next,
        phone,
        purpose,
      },
      {
        onSuccess: (data) => {
          if (normalizeChannel(data.channel) !== normalizeChannel(next)) {
            // Selected channel must equal sent channel. Never accept a silent switch.
            const msg = `Вы выбрали ${otpChannelLabel(next)}, а сервер ответил ${otpChannelLabel(
              data.channel,
            )}. Код этим способом не отправлен, выберите способ еще раз.`;
            setLocalError(msg);
            unlockAfterFailure(msg);
            return;
          }
          applyStart(data);
          if (data.stub) {
            const msg =
              'Код не ушел провайдеру: API в режиме заглушки. Проверьте ключи SIGMA.';
            setLocalError(msg);
            unlockAfterFailure(msg);
            return;
          }
          if (!options?.silent) {
            if (screen) setSentNote(data.message || '');
            else addNotification(data.message, 'success');
          }
        },
        onError,
      },
    );
  };

  useEffect(() => {
    // Provider status of the last send: shows "в очереди / доставлено / не прошло модерацию".
    if (!challengeId) return undefined;
    let cancelled = false;
    let tries = 0;
    let timer: number | undefined;
    setDeliveryNote('');
    const tick = async () => {
      tries += 1;
      try {
        const st = await fetchOtpStatus(challengeId, guest);
        if (cancelled) return;
        if (st.channel) setChannel(st.channel);
        if (st.status === 'failed') {
          setDeliveryNote('');
          setLocalError(st.message);
          setActionsUnlocked(true);
          setSecondsLeft(0);
          return;
        }
        if (st.message) setDeliveryNote(st.message);
        if (st.status === 'delivered' || st.status === 'stub') return;
      } catch {
        return; // challenge verified/expired or network: stop quietly
      }
      if (tries < 20 && !cancelled) timer = window.setTimeout(tick, 15000);
    };
    timer = window.setTimeout(tick, 5000);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [challengeId, guest]);

  const channelsBusy = resend.isPending;
  const canActNow =
    actionsUnlocked || expired || !challengeId || secondsLeft <= 0;
  const canSwitchChannel = !channelsBusy && canActNow;
  const canResend = !channelsBusy && canActNow;
  const activeChannel = normalizeChannel(channel);
  const lockReason = channelsBusy
    ? 'Отправляем код...'
    : !canActNow && secondsLeft > 0
      ? `Сменить способ можно через ${secondsLeft} с`
      : '';

  useEffect(() => {
    // Timer expired: unlock a manual resend. The voice-to-SMS step is done by the API.
    if (secondsLeft !== 0 || !challengeId || expired || channelsBusy || actionsUnlocked) return;
    const key = `${challengeId}:unlock`;
    if (autoCascadeKey.current === key) return;
    autoCascadeKey.current = key;
    setActionsUnlocked(true);
  }, [secondsLeft, challengeId, expired, channelsBusy, actionsUnlocked]);

  const statusText = (() => {
    if (expired) {
      return `Код истек или не найден. Нажмите "${resendLabel}" или выберите другой способ.`;
    }
    if (localError && offersAlternative(localError)) {
      return '';
    }
    if (localError && actionsUnlocked && screen) {
      // Компактно вместо синего блока «Что дальше»: тот же смысл одной строкой.
      return 'Введите код еще раз, отправьте его снова или выберите другой способ выше.';
    }
    if (localError && actionsUnlocked) {
      return 'Можно сразу отправить код снова или выбрать другой способ: звонок или SMS.';
    }
    if (!challengeId) {
      return `Нажмите "${resendLabel}", чтобы получить сообщение.`;
    }
    if (secondsLeft > 0 && !actionsUnlocked && activeChannel === 'voice') {
      return `Позвоним и робот продиктует код. Если звонок не дойдет, через ${secondsLeft} с отправим код в SMS.`;
    }
    if (secondsLeft > 0 && !actionsUnlocked) {
      return 'Введите код ниже. Если он не придет, можно отправить его еще раз тем же способом.';
    }
    return `Не пришел код? Нажмите "${resendLabel}" (тот же канал) или выберите другой способ выше.`;
  })();

  return (
    <div className={screen ? `${classes.block} ${classes.screen}` : classes.block}>
      {title && !screen ? <h2 className={classes.title}>{title}</h2> : null}
      {/* На экране кода способ виден по плашке Звонок/SMS, подсказка о таймере - ниже. */}
      {screen && challengeId ? null : (
        <p className={classes.text}>
          {challengeId
            ? `Способ: ${otpChannelLabel(channel)}. ${otpChannelHint(channel)}`
            : 'Код еще не отправлен.'}
        </p>
      )}
      {deliveryNote && !localError ? (
        <p className={classes.text} role='status'>
          Статус: {deliveryNote}
        </p>
      ) : null}
      {localError ? (
        <p className={classes.error} role='alert'>
          {renderWithLinks(localError)}
        </p>
      ) : null}
      {statusText ? <p className={classes.text}>{statusText}</p> : null}
      {sentNote && screen && !localError ? (
        <p className={classes.text} role='status'>
          {sentNote}
        </p>
      ) : null}
      {actionsUnlocked && !screen && !expired && !(localError && offersAlternative(localError)) ? (
        <div className={classes.nextActions} role='status'>
          <p className={classes.nextActionsTitle}>Что дальше</p>
          <ul className={classes.nextActionsList}>
            <li>Введите код еще раз, если ошиблись</li>
            <li>
              Нажмите <strong>{resendLabel}</strong>
            </li>
            <li>Или сразу переключите способ: звонок или SMS</li>
          </ul>
        </div>
      ) : null}
      <div className={classes.otpWrap}>
        <OTPInput
          key={inputKey}
          length={OTP_CODE_LENGTH}
          value={code}
          onChange={(next) => {
            setLocalError('');
            setCode(next);
          }}
          onComplete={setCode}
        />
      </div>
      <div className={classes.actions}>
        <Button
          variant='filled'
          shape='brick'
          loading={verify.isPending}
          disabled={code.length < OTP_CODE_LENGTH || !challengeId || expired}
          onClick={handleVerify}
        >
          Подтвердить
        </Button>
        <Button
          variant='outlined'
          shape='brick'
          startAdornment={screen ? undefined : <ReloadIcon />}
          loading={channelsBusy}
          disabled={!canResend}
          onClick={() =>
            sendCode((channel as OtpChannel) || fallbackChannel())
          }
        >
          {resendLabel}
        </Button>
        <fieldset className={classes.channelGroup}>
          <legend className={classes.channelLegend}>Способ получения кода</legend>
          <div className={classes.channels}>
            {CHANNELS.map((item) => {
              const on = item.id === activeChannel;
              return (
                <button
                  key={item.id}
                  type='button'
                  className={on ? `${classes.channel} ${classes.channelActive}` : classes.channel}
                  aria-pressed={on}
                  aria-describedby={lockReason ? channelHintId : undefined}
                  data-tooltip-id={tooltipId}
                  data-tooltip-content={lockReason ? `${item.hint}. ${lockReason}` : item.hint}
                  disabled={!canSwitchChannel}
                  onClick={() => sendCode(item.id)}
                >
                  <span className={classes.channelIcon} aria-hidden='true'>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
          {lockReason ? (
            <p id={channelHintId} className={classes.channelHint} aria-live='polite'>
              {lockReason}
            </p>
          ) : null}
        </fieldset>
      </div>
      {screen && footer ? <div className={classes.footer}>{footer}</div> : null}
      <Tooltip id={tooltipId} className={classes.tooltip} place='top' />
    </div>
  );
};
